import { Button, Icon, Tooltip } from '@lightspeed/design-system-react'
import { ApiRetailerPaymentType } from '@vend/types'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { T, useT } from '@transifex/react'
import { useAppSelector } from '../../../state/hooks'
import { selectOutstandingBalance } from '../../../state/slices/currentSale/currentSaleSlice'
import {
  useHotkeysService,
  useRegisterManager,
  useWebRegisterStatusManager,
} from '../../../ng-compat/hooks'
import { EVENTS } from '../../../constants'
import { getMinimumLsPayAmount } from '../../../util/lspay-utils'

export interface Props {
  paymentType?: ApiRetailerPaymentType
  disabled?: boolean
  pay: (payment: ApiRetailerPaymentType) => void
  amountTendered?: number | string
}

export const LSPayButton: FunctionComponent<Props> = ({
  paymentType,
  disabled,
  pay,
  amountTendered,
}) => {
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const registerManager = useRegisterManager()
  const [isOffline, setIsOffline] = useState<boolean>(
    !webRegisterStatusManager.isOnline()
  )
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const hotkeysService = useHotkeysService()
  const t = useT()

  // Get currency-specific minimum amount from active outlet
  const { minimumAmount, currencySymbol } = getMinimumLsPayAmount(
    registerManager.getActiveOutlet()
  )

  // Use amountTendered if provided, otherwise fall back to outstandingBalance
  const paymentAmount =
    amountTendered !== undefined &&
    amountTendered !== null &&
    amountTendered !== ''
      ? Number(amountTendered)
      : outstandingBalance
  const isAmountBelowMinimum =
    paymentAmount >= 0 && paymentAmount < minimumAmount

  const handlePay = useCallback(() => {
    if (!paymentType) {
      return
    }

    pay(paymentType)
  }, [paymentType, pay])

  useEffect(() => {
    webRegisterStatusManager.on(EVENTS.transitionOnline, () =>
      setIsOffline(false)
    )
    webRegisterStatusManager.on(EVENTS.transitionOffline, () =>
      setIsOffline(true)
    )
  }, [setIsOffline, webRegisterStatusManager])

  useEffect(() => {
    const config = {
      summary: t('Select payment option 1', {
        _context: 'Hot key summary for the Lightspeed Payments pay button',
      }),
      combo: `alt+1`,
      count: true,
      action: handlePay,
    }
    hotkeysService.add(config)

    return () => {
      hotkeysService.remove(config.combo)
    }
  }, [t, hotkeysService, handlePay])

  if (!paymentType) {
    return
  }

  const tooltipMessage = isAmountBelowMinimum ? (
    <T
      _str="Payment amount must be at least {minAmount}"
      _context="tooltip message for lightspeed payments minimum amount"
      minAmount={`${currencySymbol}${minimumAmount.toFixed(2)}`}
      lineBreak={<br />}
    />
  ) : (
    <T
      _str="You need to be connected to the internet to take payments using this payment type."
      _context="tooltip message for lightspeed payments when offline"
      lineBreak={<br />}
    />
  )

  const tooltipHeader = isAmountBelowMinimum
    ? t('Minimum payment amount required', {
        _context: 'tooltip header for lightspeed payments minimum amount',
      })
    : t('You can’t use this payment type while offline.', {
        _context: 'tooltip message for lightspeed payments when offline',
      })

  return (
    <Tooltip
      className="wr-tooltip"
      direction="center"
      message={tooltipMessage}
      header={tooltipHeader}
      canBeShown={isOffline || isAmountBelowMinimum}
      icon="info-circle"
    >
      <Button
        onClick={handlePay}
        variant={outstandingBalance < 0 ? 'no' : 'go'}
        className="vd-btn vd-btn--jumbo vd-pt5 vd-pb5 vd-mt4"
        data-hotkey-tip="1"
        disabled={!!disabled || isOffline || isAmountBelowMinimum}
        data-track={`payment-type-${paymentType.name}`}
      >
        <Icon icon="credit-card" />
        <span className="vd-ml2">{paymentType.name}</span>
      </Button>
    </Tooltip>
  )
}
